import React, {
  ComponentProps,
  FocusEventHandler,
  MouseEventHandler,
  ReactElement,
} from 'react';

import { safePolygon } from '@floating-ui/react';
import PropTypes from 'prop-types';

import { noop, optionalPropType } from '@adsk/alloy-react-helpers';

import { useMenuContext } from './MenuContext';
import MenuItem from './MenuItem';
import useOverlayMenuItem from './useOverlayMenuItem';
import { menuCategoryShape } from '../helpers/propTypes';
import { MenuCategoryType, MenuItemType, SharedProps } from '../helpers/types';
import Menu from '../Menu';

const defaultRenderItem = (props: ComponentProps<typeof MenuItem>) => (
  <MenuItem {...props} />
);

type OverlayMenuItemProps<TKey extends string | number> = Omit<
  ComponentProps<typeof MenuItem>,
  'nested' | 'onChange' | 'value' | 'defaultValue' | 'as' | 'label'
> &
  SharedProps<TKey> & {
    itemKey: TKey;
    nested?: MenuItemType<TKey>['nested'];
    category?: MenuCategoryType<TKey>;
    onClose?: () => void;
    renderItem?: (props: ComponentProps<typeof MenuItem>) => ReactElement;
    /* Options that modify how the safe polygon for nested menus behaves (see floating-ui documentation https://floating-ui.com/docs/shift) */
    safePolygonArgs?: Parameters<typeof safePolygon>[0];
  };

/** An OverlayMenuItem component which handles nested items */
const OverlayMenuItem = <TKey extends string | number>({
  itemKey,
  nested,
  category,
  defaultValue,
  value = [],
  onChange = noop,
  onClose,
  onClick,
  renderItem = defaultRenderItem,
  safePolygonArgs,
  onFocus,
  onBlur,
  onMouseEnter,
  style,
  ...rest
}: OverlayMenuItemProps<TKey>) => {
  const menuContext = useMenuContext();
  const { isOpen, getOverlayMenuItemProps, getNestedMenuProps } =
    useOverlayMenuItem({
      placement: 'right-start',
      disabled: rest.disabled,
      nested,
      itemKey,
      category,
      onClick,
      onChange,
      onClose,
      onFocus,
      onBlur,
      onMouseEnter,
      value,
      safePolygonArgs,
    });

  const highlighted = menuContext?.focusedItemKey === itemKey && !rest.disabled;
  const overlayProps = getOverlayMenuItemProps(rest);

  const handleFocus: FocusEventHandler<HTMLButtonElement> &
    FocusEventHandler<HTMLAnchorElement> = (
    e: React.FocusEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    if (menuContext && !rest.disabled) {
      menuContext.setFocusedItemKey(itemKey);
    }
    overlayProps.onFocus?.(e);
  };

  const handleBlur: FocusEventHandler<HTMLButtonElement> &
    FocusEventHandler<HTMLAnchorElement> = (
    e: React.FocusEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    if (menuContext && menuContext.focusedItemKey === itemKey) {
      menuContext.setFocusedItemKey(null);
    }
    overlayProps.onBlur?.(e);
  };

  const handleMouseEnter: MouseEventHandler<HTMLButtonElement> &
    MouseEventHandler<HTMLAnchorElement> = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    if (menuContext && !rest.disabled) {
      if (menuContext.isKeyboardNavigation) {
        menuContext.setIsKeyboardNavigation(false);
      } else {
        menuContext.setFocusedItemKey(itemKey);
      }
    }
    overlayProps.onMouseEnter?.(e);
  };

  return renderItem({
    style,
    ...overlayProps,
    'data-testid': `menu-item--${itemKey}`,
    nested: !!nested,
    showSelection: category?.showSelection,
    highlighted,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onMouseEnter: handleMouseEnter,
    children:
      nested && isOpen ? (
        <Menu
          {...getNestedMenuProps()}
          options={nested}
          defaultValue={defaultValue}
          value={value}
          onChange={onChange}
          onClose={onClose}
        />
      ) : undefined,
  });
};

OverlayMenuItem.displayName = 'MenuItemWrapper';

OverlayMenuItem.propTypes = {
  ...MenuItem.propTypes,
  /** Item key for the option has to be unique among all categories */
  itemKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /** The category of this item */
  category: menuCategoryShape,
  /** nested menu items */
  nested: PropTypes.array,
  /** Pre selected keys */
  defaultValue: optionalPropType(
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    ),
  ),
  /** Value for controlled usage */
  value: optionalPropType(
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    ),
  ),
  /** Handle for item click event */
  onChange: PropTypes.func,
  /** Handle closing of the menu */
  onClose: PropTypes.func,
  /** render custom menu item */
  renderItem: PropTypes.elementType,
};

export default OverlayMenuItem as <TKey extends string | number>(
  p: OverlayMenuItemProps<TKey>,
) => ReactElement;
